// src/presentation/hooks/useAdminUsers.ts
import {useState, useCallback} from "react";
import AdminUserService from "../../core/services/AdminUserService";
import {extractErrorMessage} from "../../utils/errorHandler";

// Tipo de usuario para el componente AdminUsersPage
export interface AdminUserData {
	id: number;
	name: string;
	email: string;
	role: "customer" | "seller" | "admin" | "payment";
	status: "active" | "blocked";
	lastLogin: string;
	registeredDate: string;
	ordersCount: number;
}

// Tipo para opciones de filtrado
interface FilterOptions {
	role?: string;
	status?: string;
	searchTerm?: string;
}

/**
 * Hook personalizado para gestión de usuarios desde administración
 */
export const useAdminUsers = () => {
	const [users, setUsers] = useState<AdminUserData[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [pagination, setPagination] = useState({
		currentPage: 1,
		totalPages: 5,
		totalItems: 0,
		itemsPerPage: 10,
	});

	// Instancia del servicio
	const adminUserService = new AdminUserService();

	/**
	 * Mapea el usuario de la API al formato esperado por el componente
	 */
	const mapApiUserToAdminUserData = (apiUser: any): AdminUserData => {
		// Determinar el rol
		let role: "customer" | "seller" | "admin" | "payment" = "customer";
		if (apiUser.is_admin) role = "admin";
		else if (apiUser.is_seller) role = "seller";
		else if (apiUser.is_payment_user) role = "payment";

		// Mapear el estado (is_blocked -> status)
		const status: "active" | "blocked" = apiUser.is_blocked
			? "blocked"
			: "active";

		return {
			id: apiUser.id,
			name: apiUser.name,
			email: apiUser.email,
			role,
			status,
			lastLogin: apiUser.last_login_at || "N/A",
			registeredDate: apiUser.created_at,
			ordersCount: apiUser.orders_count || 0,
		};
	};

	/**
	 * Carga la lista de usuarios desde la API
	 */
	const fetchUsers = useCallback(async (page = 1, perPage = 10) => {
		setLoading(true);
		setError(null);

		try {
			const response = await adminUserService.getUsers(page, perPage);

			// Mapear usuarios al formato esperado por el componente
			const mappedUsers = response.data.map(mapApiUserToAdminUserData);

			setUsers(mappedUsers);
			setPagination({
				currentPage: response.meta.current_page,
				totalPages: response.meta.last_page,
				totalItems: response.meta.total,
				itemsPerPage: response.meta.per_page,
			});

			return mappedUsers;
		} catch (err) {
			const errorMsg = extractErrorMessage(
				err,
				"Error al obtener la lista de usuarios"
			);
			console.error("Error fetching users:", errorMsg);
			setError(errorMsg);
			return [];
		} finally {
			setLoading(false);
		}
	}, []);

	/**
	 * Bloquea o desbloquea un usuario
	 */
	const toggleUserStatus = useCallback(
		async (userId: number, isCurrentlyBlocked: boolean) => {
			setLoading(true);
			setError(null);

			try {
				let success;

				if (isCurrentlyBlocked) {
					// Si está bloqueado, desbloqueamos
					success = await adminUserService.unblockUser(userId);
				} else {
					// Si no está bloqueado, bloqueamos
					success = await adminUserService.blockUser(userId);
				}

				if (success) {
					// Actualizar el estado del usuario en la lista local
					setUsers((prevUsers) =>
						prevUsers.map((user) =>
							user.id === userId
								? {
										...user,
										status: isCurrentlyBlocked ? "active" : "blocked",
									}
								: user
						)
					);
					return true;
				} else {
					setError(
						`No se pudo ${isCurrentlyBlocked ? "desbloquear" : "bloquear"} al usuario`
					);
					return false;
				}
			} catch (err) {
				const errorMsg = extractErrorMessage(
					err,
					`Error al ${isCurrentlyBlocked ? "desbloquear" : "bloquear"} al usuario`
				);
				console.error("Error toggling user status:", errorMsg);
				setError(errorMsg);
				return false;
			} finally {
				setLoading(false);
			}
		},
		[]
	);

	/**
	 * Envía un correo de restablecimiento de contraseña
	 */
	const sendPasswordReset = useCallback(async (userId: number) => {
		setLoading(true);
		setError(null);

		try {
			const success = await adminUserService.sendPasswordResetEmail(userId);

			if (!success) {
				setError(
					"No se pudo enviar el correo de restablecimiento de contraseña"
				);
			}

			return success;
		} catch (err) {
			const errorMsg = extractErrorMessage(
				err,
				"Error al enviar correo de restablecimiento"
			);
			console.error("Error sending password reset:", errorMsg);
			setError(errorMsg);
			return false;
		} finally {
			setLoading(false);
		}
	}, []);


	/**
	 * Elimina un usuario del sistema
	 */
	const deleteUser = useCallback(async (userId: number) => {
		setLoading(true);
		setError(null);

		try {
			const success = await adminUserService.deleteUser(userId);

			if (success) {
				console.log('User deleted successfully, determining optimal page to reload...');
				
				// Contar cuántos usuarios quedan en la página actual después de eliminar
				const currentUsersInPage = users.length;
				const isLastUserInPage = currentUsersInPage === 1;
				const isNotFirstPage = pagination.currentPage > 1;
				
				// Determinar a qué página ir después de la eliminación
				let targetPage = pagination.currentPage;
				if (isLastUserInPage && isNotFirstPage) {
					targetPage = pagination.currentPage - 1;
					console.log(`Last user in page ${pagination.currentPage}, moving to page ${targetPage}`);
				}
				
				// Recargar los datos desde el servidor con la página objetivo
				try {
					await fetchUsers(targetPage, pagination.itemsPerPage);
					console.log(`Data successfully reloaded from server after deletion on page ${targetPage}`);
				} catch (refreshError) {
					console.error('Error reloading data after deletion:', refreshError);
					
					// Como fallback, intentar la página 1 si falló
					if (targetPage > 1) {
						console.log('Fallback: trying to load page 1');
						try {
							await fetchUsers(1, pagination.itemsPerPage);
						} catch (fallbackError) {
							console.error('Fallback failed, updating locally:', fallbackError);
							// Último recurso: actualizar solo localmente
							setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userId));
							setPagination((prevPagination) => ({
								...prevPagination,
								totalItems: Math.max(0, prevPagination.totalItems - 1),
								currentPage: Math.min(prevPagination.currentPage, Math.ceil((prevPagination.totalItems - 1) / prevPagination.itemsPerPage) || 1),
							}));
						}
					} else {
						// Si ya estamos en página 1, actualizar localmente
						setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userId));
						setPagination((prevPagination) => ({
							...prevPagination,
							totalItems: Math.max(0, prevPagination.totalItems - 1),
						}));
					}
				}

				return true;
			} else {
				setError("No se pudo eliminar al usuario");
				return false;
			}
		} catch (err) {
			const errorMsg = extractErrorMessage(
				err,
				"Error al eliminar usuario"
			);
			console.error("Error deleting user:", errorMsg);
			setError(errorMsg);
			return false;
		} finally {
			setLoading(false);
		}
	}, [fetchUsers, pagination.currentPage, pagination.itemsPerPage, users.length]);

	/**
	 * Función para recargar manualmente los datos
	 */
	const refreshData = useCallback(async () => {
		await fetchUsers(pagination.currentPage, pagination.itemsPerPage);
	}, [fetchUsers, pagination.currentPage, pagination.itemsPerPage]);

	// Filtrar usuarios según criterios
	const filterUsers = useCallback(
		(options: FilterOptions) => {
			const {role, status, searchTerm} = options;

			return users.filter((user) => {
				// Filtro por rol
				const matchesRole = !role || role === "all" || user.role === role;

				// Filtro por estado
				const matchesStatus =
					!status || status === "all" || user.status === status;

				// Filtro por término de búsqueda
				const matchesSearch =
					!searchTerm ||
					user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
					user.email.toLowerCase().includes(searchTerm.toLowerCase());

				return matchesRole && matchesStatus && matchesSearch;
			});
		},
		[users]
	);

	/**
	 * Cambiar rol de usuario de manera centralizada
	 */
	const changeUserRole = useCallback(
		async (
			userId: number,
			role: 'customer' | 'seller' | 'admin' | 'payment',
			storeData?: {store_name: string; description?: string}
		) => {
			setLoading(true);
			setError(null);

			try {
				const success = await adminUserService.changeUserRole(userId, role, storeData);

				if (success) {
					await refreshData();
				}

				return success;
			} catch (err) {
				const errorMessage = extractErrorMessage(err);
				setError(errorMessage);
				console.error("Error al cambiar rol de usuario:", err);
				return false;
			} finally {
				setLoading(false);
			}
		},
		[refreshData]
	);

	return {
		users,
		loading,
		error,
		pagination,
		fetchUsers,
		toggleUserStatus,
		sendPasswordReset,
		changeUserRole,
		deleteUser,
		refreshData,
		filterUsers,
	};
};

export default useAdminUsers;
